import React, { FC } from "react";
import { Stack } from "@mui/material";

import SEO from "@src/components/SEO";
import Layout from "@components/Layout";

import ArticleCards from "@src/components/ArticleCards";
import BaseHero from "@src/components/Hero/base";
import SimpleHero from "@src/components/Hero/simple";
import ContentGroup from "@components/ContentGroup";
import ServiceHero from "@components/ServiceHero";
import SubNav from "@components/SubNav";
import SummarySection from "@components/SummarySection";
import TextBlock from "@src/components/TextBlock";
import CardList from "@components/CardList";
import TryPangea from "@components/CTAs/TryPangea";
import HowItWorks from "@components/HowItWorks";
import BreakOut from "@components/BreakOut";
import CaseStudy from "@src/features/CaseStudy";
import Article from "@src/features/Article";
import AboutPangea from "@src/content/AboutPangea";
import AboutGeneric from "@src/content/AboutGeneric";
import CommonPage from "@src/features/CommonPage";
import RegisterCTA from "@src/components/CTAs/register";
import UseCase from "@src/features/UseCase";
import HowItWorksHorizontal from "@src/components/HowItWorksHorizontal";
import { CardGrid } from "@src/components/Content";
import DeploymentModels from "@src/components/DeploymentModels";

const ComponentMap: { [key: string]: any } = {
  ArticleCards: ArticleCards,
  BaseHero: BaseHero,
  SimpleHero: SimpleHero,
  ContentGroup: ContentGroup,
  SummarySection: SummarySection,
  ServiceHero: ServiceHero,
  Article: Article,
  CaseStudy: CaseStudy,
  Page: CommonPage,
  SubNav: SubNav,
  TextBlock: TextBlock,
  CardList: CardList,
  CardGrid: CardGrid,
  HowItWorksHorizontal: HowItWorksHorizontal,
  HowItWorks: HowItWorks,
  Breakout: BreakOut,
  AboutGeneric: AboutGeneric,
  TryPangea: TryPangea,
  AboutPangea: AboutPangea,
  RegisterCTA: RegisterCTA,
  UseCase: UseCase,
  DeploymentModels: DeploymentModels,
};

interface PageProps {
  pageContext: any;
}

export interface PageContextMeta {
  path?: string;
}

const processContent = (
  content: Array<any>,
  pageContextMeta: PageContextMeta = {},
) => {
  const sections = content.map((e: any, i: number) => {
    if (e.component in ComponentMap) {
      const Component = ComponentMap[e.component];

      const content = "content" in e ? { ...e.content } : {};
      const meta = "meta" in e ? { ...e.meta } : {};
      const props = {
        ...content,
        ...meta,
        ...{
          pageContextMeta: pageContextMeta,
        },
      };

      if ("type" in meta && meta.type === "container") {
        return (
          <Component key={`component-${i}`} {...props}>
            {processContent(e.content, pageContextMeta)}
          </Component>
        );
      }

      return <Component key={`component-${i}`} {...props} />;
    }
  });

  return sections;
};

const BasePage: FC<PageProps> = ({ pageContext }) => {
  const content = processContent(pageContext.content, {
    path: pageContext.sitePath,
  });
  const reverseColor = pageContext.layout?.reverseColor ?? false;
  const noHeaderBg = pageContext.layout?.noHeaderBg ?? false;
  const pageUtms = pageContext?.pageUtms ?? {};

  return (
    <Layout
      reverseColor={reverseColor}
      noHeaderBg={noHeaderBg}
      pageUtms={pageUtms}
    >
      <Stack
        maxWidth="100%"
        sx={{
          position: "relative",
          backgroundColor: "#FFF",
          zIndex: "2",
          paddingBottom: "64px",
        }}
      >
        {content}
      </Stack>
    </Layout>
  );
};

export const Head: FC<PageProps> = ({ pageContext }) => (
  <SEO
    title={`${pageContext.title} | Pangea`}
    description={pageContext.description}
    heroImage={pageContext.image}
    pathname={`/${pageContext.sitePath}`}
  />
);

export default BasePage;
